import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface InsightBlockProps {
  insights: string[];
  onExpandClick?: () => void;
}
export function InsightBlock({
  insights,
  onExpandClick
}: InsightBlockProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded && onExpandClick) {
      onExpandClick();
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: -10,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.3,
    ease: 'easeOut'
  }} className="mb-4 relative" data-id="element-325">
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 rounded-lg blur-xl" data-id="element-326" />
      {/* Main content */}
      <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-blue-500/30 rounded-lg p-4 shadow-lg" data-id="element-327">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3" data-id="element-328">
          <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-329">
            <SparklesIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-330" />
          </div>
          <h4 className="text-sm font-semibold text-blue-300" data-id="element-331">Key Insights</h4>
          <div className="flex-1" data-id="element-332" />
          <span className="text-xs text-slate-500 font-mono" data-id="element-333">
            {insights.length} highlights
          </span>
        </div>
        {/* Insights list */}
        <AnimatePresence mode="wait" data-id="element-334">
          <motion.ul key={isExpanded ? 'expanded' : 'collapsed'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="space-y-2" data-id="element-335">
            {(isExpanded ? insights : insights.slice(0, 3)).map((insight, index) => <motion.li key={index} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.2,
            delay: index * 0.05
          }} className="flex items-start gap-2 text-sm text-slate-200" data-id="element-336">
                  <span className="text-blue-400 font-bold mt-0.5" data-id="element-337">•</span>
                  <span className="flex-1 leading-relaxed" data-id="element-338">{insight}</span>
                </motion.li>)}
          </motion.ul>
        </AnimatePresence>
        {/* Expand button */}
        {insights.length > 3 && <button onClick={handleToggle} className="mt-3 flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors group" data-id="element-339">
            {isExpanded ? <>
                <ChevronUpIcon className="w-3.5 h-3.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-340" />
                Show less
              </> : <>
                <ChevronDownIcon className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition-transform" data-id="element-341" />
                Show {insights.length - 3} more insight
                {insights.length - 3 > 1 ? 's' : ''}
              </>}
          </button>}
        {/* Divider */}
        <div className="mt-4 pt-3 border-t border-slate-700/50 flex items-center justify-center" data-id="element-342">
          <button onClick={onExpandClick} className="text-xs text-slate-400 hover:text-slate-300 transition-colors flex items-center gap-1 group" data-id="element-343">
            <span data-id="element-344">View Full Response</span>
            <ChevronDownIcon className="w-3 h-3 group-hover:translate-y-0.5 transition-transform" data-id="element-345" />
          </button>
        </div>
      </div>
    </motion.div>;
}